/**
 * The world leaderboards.
 *
 * Two selectors — the period and the metric — over one ranked table of *players*, plus the
 * viewer's own line pinned under it whether or not they are on the page. Below that, the older
 * feed of the biggest single *runs* ever recorded, which is a different question and stays.
 *
 * ## The clock says UTC out loud
 *
 * Every period here is cut in UTC (`leaderboardPeriodStartMs`), which is the only way one world
 * board can mean the same thing to everybody on it — so the window is printed in UTC too. A page
 * that computed "today" in London and drew it in the viewer's zone would show a heading and a
 * table that disagreed for most of the planet for most of the day.
 *
 * ## On a phone
 *
 * Both selectors wrap rather than scroll, so no option is ever off the edge; the table drops its
 * two context columns under `sm` and keeps rank, name and the number the board is actually about.
 */

import {
  LEADERBOARD_METRICS,
  LEADERBOARD_PERIODS,
  type LeaderboardMetric,
  type LeaderboardPeriod,
  PAGINATION,
} from '@chompdome/shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { METRIC_LABELS, PERIOD_LABELS } from '@/lib/leaderboards';
import { trpc } from '@/lib/trpc';

export const Route = createFileRoute('/leaderboard')({
  component: LeaderboardPage,
});

const PAGE_SIZE = PAGINATION.DEFAULT_LIMIT;
const RUNS_PAGE_SIZE = 25;

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/** The window, in UTC, spelled out — because the boards are cut there and nowhere else. */
function formatWindow(startsAt: string | null): string {
  if (startsAt === null) return 'Every run ever recorded.';
  const start = new Date(startsAt);
  return `Since ${start.toUTCString().replace('GMT', 'UTC')}.`;
}

function LeaderboardPage() {
  return (
    <div className="flex flex-1 justify-center p-4">
      <div className="w-full max-w-3xl space-y-4 self-start">
        <WorldBoard />
        <BestRuns />
      </div>
    </div>
  );
}

function WorldBoard() {
  const [period, setPeriod] = useState<LeaderboardPeriod>('day');
  const [metric, setMetric] = useState<LeaderboardMetric>('mass');
  // Reset by `select` on every change of either selector: page 3 of yesterday's chomps board
  // means nothing on this month's mass board.
  const [offset, setOffset] = useState(0);

  const board = trpc.leaderboard.board.useQuery({ period, metric, offset, limit: PAGE_SIZE });

  const items = board.data?.items ?? [];
  const total = board.data?.total ?? 0;
  const viewer = board.data?.viewer ?? null;
  const labels = METRIC_LABELS[metric];

  const select = (next: { period?: LeaderboardPeriod; metric?: LeaderboardMetric }) => {
    if (next.period) setPeriod(next.period);
    if (next.metric) setMetric(next.metric);
    setOffset(0);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>World leaderboard</CardTitle>
        <CardDescription>
          {labels.blurb} {board.data ? formatWindow(board.data.startsAt) : ''}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Selector
          label="Period"
          options={LEADERBOARD_PERIODS.map((id) => ({ id, label: PERIOD_LABELS[id] }))}
          selected={period}
          onSelect={(id) => select({ period: id })}
        />
        <Selector
          label="Board"
          options={LEADERBOARD_METRICS.map((id) => ({ id, label: METRIC_LABELS[id].tab }))}
          selected={metric}
          onSelect={(id) => select({ metric: id })}
        />

        {board.error ? (
          <p className="py-8 text-center text-sm text-destructive" role="alert">
            {board.error.message}
          </p>
        ) : board.isLoading ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
        ) : items.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Nobody has scored on this board yet. Be the first.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <caption className="sr-only">
                {PERIOD_LABELS[period]}: {labels.tab}
              </caption>
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-2 font-medium">#</th>
                  <th className="py-2 pr-2 font-medium">Player</th>
                  <th className="py-2 pr-2 text-right font-medium">{labels.column}</th>
                  <th className="hidden py-2 text-right font-medium sm:table-cell">Runs</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr
                    key={row.userId}
                    className={`border-b border-border/60 last:border-0 ${
                      row.userId === viewer?.userId ? 'bg-primary/10' : ''
                    }`}
                  >
                    <td className="py-2 pr-2 tabular-nums text-muted-foreground">{row.rank}</td>
                    <td className="py-2 pr-2 font-medium">{row.displayName}</td>
                    <td className="py-2 pr-2 text-right tabular-nums text-primary">
                      {row.value.toLocaleString()}
                    </td>
                    <td className="hidden py-2 text-right tabular-nums text-muted-foreground sm:table-cell">
                      {row.matches.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {viewer && <ViewerStanding column={labels.column} viewer={viewer} />}

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">
              {total === 0 ? 0 : offset + 1}–{offset + items.length} of {total.toLocaleString()}
              {board.data?.rankedDeeper ? '+' : ''}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={offset === 0 || board.isFetching}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!board.data?.hasMore || board.isFetching}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface ViewerLine {
  userId: string;
  displayName: string;
  rank: number | null;
  value: number;
  matches: number;
}

/**
 * The viewer's own line, drawn under the table whether or not they are on the page — which is
 * the whole reason the server resolves a standing separately from the page of rows.
 *
 * A null rank is not an error: it means they are outside the depth the board ranks to, or scored
 * nothing on this board in this period. Their own numbers are still true, so they are shown.
 */
function ViewerStanding({ column, viewer }: { column: string; viewer: ViewerLine }) {
  return (
    <section
      className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 rounded-md border border-primary/40 bg-primary/10 px-3 py-2"
      aria-label="Your standing"
    >
      <span className="text-sm font-medium">
        {viewer.rank === null ? 'You — unranked' : `You — #${viewer.rank.toLocaleString()}`}
      </span>
      <span className="text-sm tabular-nums">
        <span className="text-muted-foreground">{column}: </span>
        <span className="font-semibold text-primary">{viewer.value.toLocaleString()}</span>
        <span className="text-muted-foreground"> · {viewer.matches.toLocaleString()} runs</span>
      </span>
    </section>
  );
}

interface SelectorOption<T extends string> {
  id: T;
  label: string;
}

/**
 * A row of choices. Wraps rather than scrolls: five options fit on the narrowest phone in two
 * lines, and an option that has scrolled off the edge is an option nobody finds.
 */
function Selector<T extends string>({
  label,
  options,
  selected,
  onSelect,
}: {
  label: string;
  options: readonly SelectorOption<T>[];
  selected: T;
  onSelect: (id: T) => void;
}) {
  return (
    <fieldset className="flex flex-wrap gap-2">
      <legend className="sr-only">{label}</legend>
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          aria-pressed={selected === option.id}
          onClick={() => onSelect(option.id)}
          className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${
            selected === option.id
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-border text-muted-foreground hover:bg-muted'
          }`}
        >
          {option.label}
        </button>
      ))}
    </fieldset>
  );
}

/**
 * The biggest single runs ever recorded — one row per *match*, not per player, and keyset-paged
 * so a run finishing between two page loads cannot shift rows across the boundary. It answers a
 * different question from the board above it and is left exactly as it was.
 */
function BestRuns() {
  const query = trpc.leaderboard.top.useInfiniteQuery(
    { limit: RUNS_PAGE_SIZE },
    { getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined },
  );

  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Biggest single runs</CardTitle>
        <CardDescription>One row per match, ever recorded in the dome.</CardDescription>
      </CardHeader>
      <CardContent>
        {query.isLoading ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
        ) : query.error ? (
          <p className="py-8 text-center text-sm text-destructive" role="alert">
            {query.error.message}
          </p>
        ) : rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Nobody has finished a match yet. Be the first.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-2 font-medium">#</th>
                  <th className="py-2 pr-2 font-medium">Player</th>
                  <th className="py-2 pr-2 text-right font-medium">Mass</th>
                  <th className="hidden py-2 pr-2 text-right font-medium sm:table-cell">Kills</th>
                  <th className="hidden py-2 text-right font-medium sm:table-cell">Survived</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row.id} className="border-b border-border/60 last:border-0">
                    <td className="py-2 pr-2 tabular-nums text-muted-foreground">{index + 1}</td>
                    <td className="py-2 pr-2 font-medium">{row.displayName}</td>
                    <td className="py-2 pr-2 text-right tabular-nums text-primary">
                      {row.finalMass.toLocaleString()}
                    </td>
                    <td className="hidden py-2 pr-2 text-right tabular-nums sm:table-cell">
                      {row.kills}
                    </td>
                    <td className="hidden py-2 text-right tabular-nums text-muted-foreground sm:table-cell">
                      {formatDuration(row.durationSeconds)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {query.hasNextPage && (
          <div className="mt-4 flex justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => query.fetchNextPage()}
              disabled={query.isFetchingNextPage}
            >
              {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
